import {HttpClient, HttpParams} from '@angular/common/http';
import {Injectable} from '@angular/core';
import {BackendConfiguration} from '@application/configuration/backend-configuration';
import {IdName} from '@domain/id-name';
import {MachineGroup} from '@domain/machine-group/machine-group';
import {MachineGroupOverviewList} from '@presentation/pages/machine-overview/sidebar/group-editor/machine-group-overview-list';
import {AssertionUtils} from '@vdw/angular-component-library';
import {Observable, of as observableOf} from 'rxjs';
import {map, switchMap, tap} from 'rxjs/operators';
import {MachineGroups} from './machine-groups';

@Injectable()
export class HttpMachineGroupsService implements MachineGroups {
  private httpClient: HttpClient;
  private backendConfiguration: BackendConfiguration;

  public constructor(httpClient: HttpClient, backendConfiguration: BackendConfiguration) {
    this.httpClient = httpClient;
    this.backendConfiguration = backendConfiguration;
  }

  public getRoot(): Observable<MachineGroupOverviewList> {
    return this.httpClient.get(`${this.backendConfiguration.getMachineEndpoint()}machinegroups`).pipe(map((machineGroupJSON: any) => MachineGroupOverviewList.fromJSON(machineGroupJSON)));
  }

  public getById(id: number): Observable<MachineGroupOverviewList> {
    return this.httpClient.get(`${this.backendConfiguration.getMachineEndpoint()}machinegroups/${id}`).pipe(map((machineGroupJSON: any) => MachineGroupOverviewList.fromJSON(machineGroupJSON)));
  }

  public save(machineGroup: MachineGroup, floorplan?: File): Observable<MachineGroupOverviewList> {
    let floorplanId: Observable<number> = observableOf(null);
    if (!AssertionUtils.isNullOrUndefined(floorplan)) {
      const formData = new FormData();
      formData.append('file', floorplan, floorplan.name);
      floorplanId = this.httpClient.post(`${this.backendConfiguration.getMachineEndpoint()}machinegroups/floorplan`, formData).pipe(map((response: {id: number}) => response.id));
    }

    return floorplanId.pipe(
      tap((imageId: number) => (machineGroup.imageId = imageId)),
      switchMap(() => this.httpClient.post(`${this.backendConfiguration.getMachineEndpoint()}machinegroups`, machineGroup)),
      map((response: {id: number}) => new MachineGroupOverviewList(response.id, machineGroup.name, machineGroup.imageId, floorplan?.name, false, [], []))
    );
  }

  public update(id: number, machineGroup: MachineGroup, updateFloorplan: boolean = true, floorplan?: File): Observable<MachineGroup> {
    let floorplanId = observableOf(machineGroup.imageId);
    if (updateFloorplan && !AssertionUtils.isNullOrUndefined(floorplan)) {
      const formData = new FormData();
      formData.append('file', floorplan, floorplan.name);
      floorplanId = this.httpClient.post(`${this.backendConfiguration.getMachineEndpoint()}machinegroups/floorplan`, formData).pipe(map((response: {id: number}) => response.id));
    }

    return floorplanId.pipe(
      tap((imageId: number) => (machineGroup.imageId = imageId)),
      switchMap(() => this.httpClient.put<void>(`${this.backendConfiguration.getMachineEndpoint()}machinegroups/${id}`, machineGroup)),
      map(() => new MachineGroup(machineGroup.name, machineGroup.imageId, machineGroup.parentGroupId))
    );
  }

  public delete(id: number): Observable<void> {
    return this.httpClient.delete<void>(`${this.backendConfiguration.getMachineEndpoint()}machinegroups/${id}`);
  }

  public move(id: number, parentGroupId: number): Observable<void> {
    return this.httpClient.patch<void>(`${this.backendConfiguration.getMachineEndpoint()}machinegroups/${id}/parent`, {parentGroupId});
  }

  public getFloorplan(floorplanId: number): Observable<Blob> {
    return this.httpClient.get(`${this.backendConfiguration.getMachineEndpoint()}machinegroups/floorplan/${floorplanId}`, {responseType: 'blob'});
  }

  public addMachineToGroup(id: number, machineId: number, oldMachineGroupId?: number): Observable<void> {
    return this.httpClient.post<void>(`${this.backendConfiguration.getMachineEndpoint()}machinegroups/${id}/machines`, {
      machineId,
      oldMachineGroupId
    });
  }

  public removeMachineFromGroup(id: number, machineId: number): Observable<void> {
    return this.httpClient.delete<void>(`${this.backendConfiguration.getMachineEndpoint()}machinegroups/${id}/machines/${machineId}`);
  }

  public isIdentifierAvailable(name: string): Observable<boolean> {
    const params = new HttpParams().set('name', name);

    return this.httpClient
      .get(`${this.backendConfiguration.getMachineEndpoint()}machinegroups/check-name`, {params})
      .pipe(map((response: {machineGroupNameAvailable: boolean}) => response.machineGroupNameAvailable));
  }

  public getCompatible(plasticProductId?: number, machineId?: number, toolId?: number): Observable<IdName[]> {
    let params = new HttpParams();
    if (plasticProductId !== undefined) {
      params = params.set('plasticProductId', plasticProductId);
    }
    if (machineId !== undefined) {
      params = params.set('machineId', machineId);
    }
    if (toolId !== undefined) {
      params = params.set('toolId', toolId);
    }

    return this.httpClient
      .get(`${this.backendConfiguration.getAggregatorEndpoint()}compatible/machine-groups`, {params})
      .pipe(map((machineGroupsJSON: any) => machineGroupsJSON.map((machineGroupJSON: any) => IdName.fromJSON(machineGroupJSON))));
  }
}
